// libraries
import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { Stack, Typography, useTheme } from '@mui/material'

// generic components
import Input from 'components/generic/form/Input'
import Checkbox from 'components/generic/form/Checkbox'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import RefreshIcon from '@mui/icons-material/Refresh'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

const FreightCostBladeFreightCost = ({
  disableFreightCost,
  disableChargeback,
  type,
  pricePrepend,
  carrierPricePrepend,
  priorityFreightCostInfo,
}) => {
  const theme = useTheme()
  const methods = useFormContext()
  const { control, formState, setValue, watch } = methods
  const { errors } = formState
  const cost = watch('cost')
  const chargeback = watch('chargeback')
  const chargebackAmount = watch('chargebackAmount')
  const negativeChargeback = watch('negativeChargeback')
  const autoCalculateFreightChargeback = watch('autoCalculateFreightChargeback')
  const autocalculateFreightCost = watch('autocalculateFreightCost')
  const shouldShowFreightCostSource = watch('shouldShowFreightCostSource')
  const shouldShowFreightChargebackSource = watch('shouldShowFreightChargebackSource')

  const ableToRefresh = useMemo(() => {
    if (!priorityFreightCostInfo) return false
    const formCost = Number(cost)
    const formChargebackAmount = negativeChargeback ? -Math.abs(Number(chargebackAmount)) : Number(chargebackAmount)
    const autocalculatedCost = Number(priorityFreightCostInfo?.freightCost)
    const autocalculatedChargebackAmount = Number(priorityFreightCostInfo?.freightChargeback)

    // Hide refresh if both freight cost and chargeback are disabled
    if (disableFreightCost && disableChargeback) return false

    // Hide refresh if freight cost and chargeback are not autocalculated, handle $0 values
    if (autocalculatedCost == null || autocalculatedChargebackAmount == null) return false

    // Show refresh if freight cost or chargeback are different from the autocalculated values
    const costDifferent = !disableFreightCost && (formCost !== autocalculatedCost || autocalculateFreightCost === false)
    const chargebackDifferent =
      !disableChargeback &&
      (formChargebackAmount !== autocalculatedChargebackAmount || autoCalculateFreightChargeback === false)

    return costDifferent || chargebackDifferent
  }, [
    priorityFreightCostInfo,
    cost,
    chargebackAmount,
    autocalculateFreightCost,
    autoCalculateFreightChargeback,
    negativeChargeback,
    disableFreightCost,
    disableChargeback,
  ])

  useEffect(() => {
    if (!priorityFreightCostInfo) {
      setValue('shouldShowFreightCostSource', false)
      setValue('shouldShowFreightChargebackSource', false)
      return
    }
    // check the pricing for the source and compare to the cost currently in the form, handle $0 values
    const signedChargebackAmount = negativeChargeback ? -Math.abs(Number(chargebackAmount)) : Number(chargebackAmount)
    const enableFreightCostSource = priorityFreightCostInfo?.freightCost === Number(cost) && autocalculateFreightCost
    const enableFreightChargebackSource =
      priorityFreightCostInfo?.freightChargeback === signedChargebackAmount &&
      autoCalculateFreightChargeback &&
      (chargeback || signedChargebackAmount === 0)
    setValue('shouldShowFreightCostSource', enableFreightCostSource)
    setValue('shouldShowFreightChargebackSource', enableFreightChargebackSource)
  }, [
    autocalculateFreightCost,
    autoCalculateFreightChargeback,
    priorityFreightCostInfo,
    ableToRefresh,
    cost,
    chargebackAmount,
    negativeChargeback,
  ])

  return (
    <Stack mt={5} spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h6" sx={{ mb: 0 }}>
          Freight Cost
        </Typography>
        {ableToRefresh ? (
          <Tooltip
            title="Automatically update cost and chargeback using the first available pricing. Order of priority: Contract, Location, Freight Lane."
            placement="right"
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton
                size="small"
                onClick={() => {
                  const costValue = Number(priorityFreightCostInfo?.freightCost ?? 0)
                  const chargebackValue = Number(priorityFreightCostInfo?.freightChargeback ?? 0)

                  // perform all necessary updates to the form
                  if (!disableFreightCost) {
                    setValue('autocalculateFreightCost', true)
                    setValue('cost', costValue, { shouldDirty: true })
                  }
                  if (!disableChargeback) {
                    setValue('chargebackAmount', chargebackValue, { shouldDirty: true })
                    setValue('chargeback', chargebackValue !== 0, {
                      shouldDirty: true,
                    })
                    setValue('negativeChargeback', chargebackValue < 0, { shouldDirty: true })
                    setTimeout(() => setValue('autoCalculateFreightChargeback', true), 0)
                  }
                }}
                aria-label="Refresh freight costs"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ mb: 0 }}>
                Refresh all pricing from {priorityFreightCostInfo?.sourceLabel}
              </Typography>
            </Stack>
          </Tooltip>
        ) : null}
      </Stack>

      <Stack direction="row" spacing={2}>
        <Input
          name="cost"
          label={'Freight Cost' + (shouldShowFreightCostSource ? ` (${priorityFreightCostInfo?.sourceLabel})` : '')}
          variant={disableFreightCost ? 'standard' : 'outlined'}
          size="small"
          fractionDigits={2}
          defaultValue={0}
          disabled={disableFreightCost}
          control={control}
          error={!!errors.cost}
          helperText={errors.cost?.message}
          InputProps={{
            inputComponent: InputFormat,
            inputProps: {
              inputType: 'price',
              pricePrepend: carrierPricePrepend,
              onChange: () => {},
              decimalScale: 2,
              allowNegative: false,
            },
            ...(disableFreightCost ? { disableUnderline: true } : {}),
          }}
          InputLabelProps={{
            ...(disableFreightCost ? { shrink: true } : {}),
          }}
          sx={{
            width: '40%',
            'input.Mui-disabled': { WebkitTextFillColor: theme.palette.text.primary },
            'label.Mui-disabled': { WebkitTextFillColor: theme.palette.text.secondary },
          }}
          onFocus={() => {
            setValue('autocalculateFreightCost', false)
            setValue('shouldShowFreightCostSource', false)
          }}
        />
        <Checkbox
          name="autocalculateFreightCost"
          label="Automatically calculate freight cost"
          defaultValue={false}
          control={control}
          disabled={disableFreightCost}
          onChange={event => {
            setValue('autocalculateFreightCost', event.target.checked)
            if (event.target.checked) {
              if (priorityFreightCostInfo?.freightCost !== null) setValue('cost', priorityFreightCostInfo?.freightCost)
              setTimeout(() => setValue('autocalculateFreightCost', true), 0)
            } else {
              setValue('shouldShowFreightCostSource', false)
            }
          }}
        />
      </Stack>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <Typography variant="h6" gutterBottom>
            Freight Chargeback
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Input
            name="chargebackAmount"
            label={
              'Chargeback Amount' +
              (shouldShowFreightChargebackSource ? ` (${priorityFreightCostInfo?.sourceLabel})` : '')
            }
            variant={disableChargeback ? 'standard' : 'outlined'}
            size="small"
            fractionDigits={2}
            defaultValue={0}
            disabled={!chargeback || disableChargeback}
            control={control}
            error={!!errors.chargebackAmount}
            helperText={errors.chargebackAmount?.message}
            onChange={() => {
              if (chargeback && autoCalculateFreightChargeback) setValue('autoCalculateFreightChargeback', false)
            }}
            onFocus={() => {
              setValue('autoCalculateFreightChargeback', false)
              setValue('shouldShowFreightChargebackSource', false)
            }}
            InputProps={{
              inputComponent: InputFormat,
              inputProps: {
                inputType: 'price',
                pricePrepend,
                onChange: () => {},
                decimalScale: 2,
                allowNegative: false,
              },
              ...(disableChargeback ? { disableUnderline: true } : {}),
            }}
            sx={{
              width: '40%',
              ...(disableChargeback
                ? {
                    'input.Mui-disabled': { WebkitTextFillColor: theme.palette.text.primary },
                    'label.Mui-disabled': { WebkitTextFillColor: theme.palette.text.secondary },
                  }
                : {}),
            }}
          />
          <Stack>
            <Checkbox
              noMargin
              name="chargeback"
              label="Enable chargeback"
              defaultValue={false}
              control={control}
              disabled={disableChargeback}
              onChange={event => {
                if (!disableChargeback) {
                  if (event.target.checked && autoCalculateFreightChargeback) {
                    setValue('chargebackAmount', priorityFreightCostInfo?.freightChargeback ?? '0.00')
                    setValue('shouldShowFreightChargebackSource', event.target.checked)
                    if (autocalculateFreightCost) {
                      setTimeout(() => setValue('autoCalculateFreightChargeback', true), 0)
                    }
                  }
                  if (!event.target.checked) {
                    setValue('chargebackAmount', '0.00')
                    setTimeout(() => setValue('autoCalculateFreightChargeback', false), 0)
                  }
                }
              }}
            />
            <Checkbox
              noMargin
              name="autoCalculateFreightChargeback"
              label="Automatically calculate chargeback"
              defaultValue={false}
              control={control}
              disabled={disableChargeback}
              onChange={event => {
                if (!disableChargeback) {
                  setValue('shouldShowFreightChargebackSource', event.target.checked)
                  if (event.target.checked) {
                    const chargebackValue = priorityFreightCostInfo?.freightChargeback ?? '0.00'
                    setValue('chargebackAmount', chargebackValue)
                    setValue('chargeback', event.target.checked, { shouldDirty: true })
                    setValue('negativeChargeback', Number(chargebackValue) < 0, { shouldDirty: true })
                    setTimeout(() => setValue('autoCalculateFreightChargeback', true), 0)
                  } else {
                    setTimeout(() => setValue('autoCalculateFreightChargeback', false), 0)
                  }
                }
              }}
            />
            {type === 'OBT' && (
              <Checkbox
                noMargin
                name="negativeChargeback"
                label="Yard will pay freight chargeback on invoice"
                defaultValue={false}
                control={control}
                disabled={disableChargeback || !chargeback}
                onChange={event => {
                  if (!disableChargeback && chargeback) {
                    setValue('autoCalculateFreightChargeback', false)
                  }
                }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

FreightCostBladeFreightCost.propTypes = {
  disableFreightCost: PropTypes.bool,
  disableChargeback: PropTypes.bool,
  freightLaneActivePrice: PropTypes.object,
  autocalculatedPrice: PropTypes.number,
  priorityFreightCostInfo: PropTypes.object,
  type: PropTypes.oneOf(['IBT', 'OBT']).isRequired,
  pricePrepend: PropTypes.string,
  carrierPricePrepend: PropTypes.string,
}

FreightCostBladeFreightCost.defaultProps = {
  disableFreightCost: false,
  disableChargeback: false,
  freightLaneActivePrice: null,
  autocalculatedPrice: 0,
  priorityFreightCostInfo: {},
  pricePrepend: '$',
  carrierPricePrepend: '$',
}

export default FreightCostBladeFreightCost
